import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import pluralize from 'pluralize';
import { distinctUntilChanged, map } from 'rxjs';

import { type CanvasElementId, type PropsOf } from '@blaze-runtime/engine-domain';
import {
  type CanvasElementPropertiesWithData,
  DEFAULT_COLUMN_TAGS_PALETTE,
  type ElementSizingMode,
  getActiveBinding,
  type IPropEditorResolver,
  isBindingActive,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_EDIT,
  PROGRESS_BAR_THRESHOLDS_COLORS,
  type PropertiesConfig,
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
  type PropertiesConfigInputOptionsToggle,
  type PropertiesConfigInputTableColumnModel,
  type PropertiesConfigInputTableConditionalFormatModel,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSubsection,
  type ResizeBehavior,
  TABLE_COLUMN_FORMATS,
  TableConditionalFormatColumnSelection,
  type WithUnifiedBulkActions,
  type WithUnifiedOtherActions,
  type WithUnifiedRowActions,
} from '@blaze-shared/elements-domain';
import { getColumnNameLatestOrUnavailable, SchemaService } from '@blaze-shared/schema-data-access';
import {
  COLUMN_ID_OR_NAME,
  type ColumnQueryLike,
  type DatasetColumn,
  findColumn,
  isColumnEditable,
} from '@blaze-shared/schema-domain';
import { isSecureTextColumn } from '@blaze-shared/shared-feature';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { getElementById } from '../../+state/elements/elements.selectors';
import { PAGINATION_COUNT_OPTIONS } from '../../services/element-pagination.service';
import { RuntimeService } from '../../services/runtime.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';
import { NUMBER_FIELD_FORMATS_OPTIONS } from '../chart/chart.models';

import { type TablePropsModel } from './table.props-model';

export const DEFAULT_WIDTH = 500;
export const DEFAULT_HEIGHT = 340;
export const MIN_SIZE = 100;
export const MAX_WIDTH = 900;
export const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
export const FREEZE_COLUMNS = [
  { label: 'First column', value: 1 },
  { label: 'First 2 columns', value: 2 },
  { label: 'First 3 columns', value: 3 },
  { label: 'First 4 columns', value: 4 },
  { label: 'First 5 columns', value: 5 },
];

export const TOOLTIP_LOCKED_FOR_API = 'Selected data source does not support this action';

export function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('table', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Data',
        [],
        [
          epc.utils.disabledField(
            epc.fields.selectDataSourceField({
              key: 'value',
              label: 'Data source',
              sources: 'list',
              bindings: [],
            }),
            (model: TablePropsModel) =>
              model.content.table.origin === 'wizard'
                ? 'Data source cannot be modified in an auto-generated details view.'
                : undefined,
          ),
          tableColumnsField('content.columns', 'data.?key:value.dataSource'),
          epc.utils.separatedField(
            epc.fields.listTableGroupByFields(
              'content.table.groupBy',
              'Group By',
              'data.?key:value.dataSource',
            ),
          ),
          epc.fields.toggleField(
            'content.table.groupByStartWithCollapsed',
            'Start with groups collapsed',
            {
              hideWhen: (model: TablePropsModel) => !model.content.table.groupBy?.[1]?.field,
            },
          ),
        ],
        { enabledByDefault: true },
      ),

      epc.subsections.subsection(
        'content.table',
        'Layout Options',
        [
          // epc.fields.toggleField('content.table.singleSelect', 'Single row selection'), // hidden until needed
          // epc.fields.selectFieldFromList('content.table.behavior', 'Behavior', TABLE_BEHAVIOR_OPTIONS, { allowNone: false, showSelection: true }),
          // todo [ls] replace `content.table.usePagination` usages with `content.table.behavior`

          epc.fields.sortField(
            'content.sort',
            'Default sort',
            'content.columns',
            'name',
            COLUMN_ID_OR_NAME,
          ),
          epc.fields.toggleField('content.table.disableUserSort', 'Allow user sorting', {
            isNegated: true,
          }),
          epc.fields.toggleField('content.table.showColumnHeaders', 'Show column headers'),
          epc.utils.observedField(
            epc.fields.toggleField('content.table.usePagination', 'Use pagination', {
              disableWhen: (model) => model.content.table.groupBy.some((group: any) => group.field),
            }),
            (model: any, value: boolean, syncModel: any) => {
              if (value && !getValueByPath(syncModel, 'content.pagination.count')) {
                return { 'content.pagination.count': PAGINATION_COUNT_OPTIONS[0] };
              }
              return {};
            },
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromList(
              'content.pagination.count',
              'Rows displayed',
              PAGINATION_COUNT_OPTIONS?.map((value) => ({ value, label: value.toString() })),
              { showSelection: true, allowNone: false },
            ),
            'content.table.usePagination',
            (value) => !value,
          ),
        ],
        {
          hideWhen: (
            _elementId: CanvasElementId,
            _resolver: IPropEditorResolver,
            model: TablePropsModel,
          ) => model.content.table.origin === 'wizard',
        },
      ),

      epc.subsections.complexSearchAndFiltersSubsection({
        hideWhen: (
          _elementId: CanvasElementId,
          resolver: IPropEditorResolver,
          model: PropsOf<'table'>,
        ) => model.content.table?.origin === 'wizard' || hasApiCallDataSource(resolver, model),
      }),

      buildActionsSubsection(),

      epc.subsections.titleContentSubsection('content'),

      epc.subsections.subsection('content.table', 'When Empty', [
        ...epc.fields.emptyStateFields({
          messagePath: 'content.table.emptyTableLabel',
          hideLabel: true,
        }),
      ]),

      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
      ]),
    ]),

    epc.section('content', 'Behavior', [
      epc.subsections.subsection('content.table', 'Always Keep Visible', [
        epc.fields.toggleField('content.table.headerAlwaysVisible', 'Header'),
        epc.fields.toggleField('content.table.footerAlwaysVisible', 'Footer'),
        epc.fields.toggleField('content.table.alwaysVisibleFirst', 'Freeze columns'),
        epc.utils.conditionalField(
          epc.fields.selectFieldFromList(
            'content.table.alwaysVisibleColumnsCount',
            '',
            FREEZE_COLUMNS,
            { hideLabel: true, allowNone: false },
          ),
          'content.table.alwaysVisibleFirst',
          (value: boolean) => !value,
        ),
        epc.utils.conditionalField(
          epc.fields.toggleField('content.table.alwaysVisibleActions', 'Action column'),
          'content',
          (content: WithUnifiedRowActions) => content.unifiedRowActions.length === 0,
        ),
      ]),

      conditionalFormattingSubsection(),

      epc.subsections.visibilitySubsection(),

      epc.subsections.visibilityColumnSubsection(),
    ]),

    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.table', 'Row', [
          epc.fields.resizeBehaviorField('style.table.rowHeightBehavior', 'Height'),
          epc.fields.genericSizeField('style.table.rowHeight', 'Padding'),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromList(
              'style.table.rowLineClamp',
              'Show',
              Array.from({ length: 10 }, (_, i) => {
                const value = i + 1;
                const label = `${value} ${pluralize('line', value)}`;
                return { label, value };
              }),
              { showSelection: true, allowNone: false },
            ),
            'style.table.rowHeightBehavior',
            (value: ResizeBehavior) => value !== 'fixed',
          ),
          epc.fields.toggleField('style.table.rowNumbering', 'Row numbering'),
        ]),

        epc.subsections.subsection('style.table', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.table.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowNone: false,
            allowGradient: false,
          }),
          epc.fields.colorField('style.header.background', 'Header background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.header.label', 'Header label', {
            allowGradient: false,
            allowNone: false,
          }),
          ...epc.fields.visualEffectField('style.general.visualEffect'),
        ]),
        epc.subsections.subsection('style.table', 'Table', [
          epc.fields.colorField('style.table.text', 'Cell text', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.table.background', 'Table background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.utils.observedField(
            epc.fields.colorField('style.table.lines', 'Table lines', {
              showTableLinesOptions: true,
              allowGradient: false,
              allowNone: false,
              allowSolid: false,
              skipChangeToNone: true,
            }),
            (_, value) => ({ 'style.table.hideColumnsBorder': value.tableLinesOptions !== 'all' }),
          ),
          epc.fields.colorField('style.table.hover', 'Row hover', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.table.selectedRow', 'Row selected', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.radiusField('style.table.radius', 'Table radius'),
          epc.utils.separator('Group Label'),
          epc.fields.colorField('style.table.group.text', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          ...epc.fields.fontFamilyAndWeightFields(
            'style.table.group.fontFamily',
            'style.table.group.fontWeight',
          ),
          epc.fields.fontStyleField('style.table.group.style', 'Style', ['bold']),
          epc.fields.sliderField('style.table.group.fontSize', 'Size', [
            { min: 8, max: 72, unit: 'px' },
          ]),
          epc.utils.separatedField(
            epc.fields.radiosField(
              'style.table.group.colorGenOptions',
              'Color variant',
              [
                { value: 'generate', label: 'Single Color' },
                { value: 'preselected', label: 'Custom Colors' },
              ],
              { hideLabel: true },
            ),
            { fullWidthSeparator: false, label: 'Group Background' },
          ),
          epc.utils.conditionalField(
            epc.fields.colorField('style.table.group.bgBaseColor', 'Base color', {
              allowGradient: false,
              allowNone: false,
            }),
            'style.table.group.colorGenOptions',
            (targetPathValue) => targetPathValue !== 'generate',
          ),
          epc.utils.conditionalField(
            epc.fields.colorField('style.table.group.bgLevel1', 'Level 1', {
              allowGradient: false,
              allowNone: false,
            }),
            'style.table.group.colorGenOptions',
            (targetPathValue, model) =>
              targetPathValue !== 'preselected' || !model?.content?.table?.groupBy?.[1]?.field,
          ),
          epc.utils.conditionalField(
            epc.fields.colorField('style.table.group.bgLevel2', 'Level 2', {
              allowGradient: false,
              allowNone: false,
            }),
            'style.table.group.colorGenOptions',
            (targetPathValue, model) =>
              targetPathValue !== 'preselected' || !model?.content?.table?.groupBy?.[2]?.field,
          ),
          epc.utils.conditionalField(
            epc.fields.colorField('style.table.group.bgLevel3', 'Level 3', {
              allowGradient: false,
              allowNone: false,
            }),
            'style.table.group.colorGenOptions',
            (targetPathValue, model) =>
              targetPathValue !== 'preselected' || !model?.content?.table?.groupBy?.[3]?.field,
          ),
        ]),
        epc.subsections.subsection('style.booleanCol', 'Checkbox', [
          epc.fields.colorField('style.booleanCol.checkedBackground', 'Checked fill', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.booleanCol.checkedMark', 'Checked mark', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.booleanCol.uncheckedBackground', 'Unchecked fill', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.booleanCol.uncheckedMark', 'Unchecked mark', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.title',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}

function buildActionsSubsection(): PropertiesConfigSubsection {
  const commonActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.utils.conditionalField(
      epc.utils.observedField(
        epc.fields.toggleField('content.table.editable', 'In-line edit', {
          conditionsPath: 'content.table.editableConditions',
          conditionsExpressionPath: 'content.table.editableExpression',
          conditionsConfig: {
            title: 'In-line Edit',
            scope: 'component',
            cyIdBase: 'FloatingTableInlineEditPanel',
          },
          disableWhen: (syncModel, params) => {
            if (params?.hasApiCallDataSource) {
              return TOOLTIP_LOCKED_FOR_API;
            }
            let result: string | undefined =
              'Columns do not meet editing criteria. Refresh data to enable editing.';
            const activeBinding = getActiveBinding((syncModel as PropsOf<'table'>).data);
            const activeBindingColumns = activeBinding?.dataSource?.columns;

            const editableColumnsKeys = inject(RuntimeService).getEditableColumns(
              (syncModel as PropsOf<'table'>).data,
            );

            (syncModel as PropsOf<'table'>).content.columns?.forEach((column) => {
              const columnSchema = findColumn(activeBindingColumns, column);
              if (isColumnEditable(columnSchema, editableColumnsKeys)) {
                result = undefined;
              }
            });
            return result;
          },
        }),
        (_, __, syncModel: PropsOf<'table'>) => {
          const result: Record<string, boolean> = {};

          const activeBinding = getActiveBinding(syncModel.data);
          const activeBindingColumns = activeBinding?.dataSource?.columns;

          const editableColumnsKeys = inject(RuntimeService).getEditableColumns(syncModel.data);

          syncModel.content.columns?.forEach((column, index) => {
            const columnSchema = findColumn(activeBindingColumns, column);
            result[`content.columns.${index}.editable`] = isColumnEditable(
              columnSchema,
              editableColumnsKeys,
            );
          });
          return result;
        },
      ),
      'content.data',
      (_, model) => {
        return !getActiveBinding(model.data);
      },
    ),

    epc.fields.toggleField('content.table.autoSelectFirstRow', 'Auto-select first row', {
      tooltip:
        'When the page is loaded and there are components linked to this table, the first row of this table will be automatically selected.',
    }),
    epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
      epc.fields.toggleField('content.itemAdd.enabled', 'Add new row', {
        disableWhen: (_syncModel, params) => {
          if (params?.hasApiCallDataSource) {
            return TOOLTIP_LOCKED_FOR_API;
          }
          return undefined;
        },
      }),
      {
        action: () => ({
          id: ITEMS_CONTAINER_ACTION_ADD,
          styled: { enabled: true, elementType: 'button' },
        }),
        pageId: (model: TablePropsModel) => model.content.itemAdd?.targetId,
        conditionsPath: 'content.itemAdd.visibilityConditions',
        conditionsExpressionPath: 'content.itemAdd.visibilityExpression',
        conditionsConfig: {
          title: 'Add new row visibility',
          scope: 'component',
        },
      },
    ),
    epc.utils.conditionalField(
      epc.fields.textInput('content.itemAdd.label', 'Add label'),
      'content.itemAdd.enabled',
      (value) => !value,
    ),
    ...epc.fields.containerRowClickActionsField('On row click', 'Edit row', {
      conditionsPath: 'content.rowClick.visibilityConditions',
      conditionsExpressionPath: 'content.rowClick.visibilityExpression',
      conditionsDisabled: (model: TablePropsModel) =>
        model.content.rowClick?.action ? undefined : '',
      conditionsConfig: {
        title: 'On row click visibility',
        scope: 'component',
      },
      optionsFilter: (_props, options, params) =>
        !params?.hasApiCallDataSource
          ? options
          : options.filter((o) => o.value !== ITEMS_CONTAINER_ACTION_EDIT),
    }),
  ];

  const rowActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.fields.unifiedActionsField('content.unifiedRowActions', 'row', {
      hasWorkflow: (action: PropertiesConfigInputContainerItemUnifiedRowActionModel) =>
        action.type === 'delete' || action.type === 'custom',
      workflowIdPath: (action: PropertiesConfigInputContainerItemUnifiedRowActionModel) =>
        action.type === 'delete' ? 'content.itemDelete.targetId' : undefined,
    }),
    ...epc.utils.conditionalFields(
      [
        epc.utils.separator('Show Actions'),
        epc.fields.actionsDisplayModeField('content.table.actionsDisplayMode', '', {
          hideLabel: true,
          leftIndent: true,
        }),
      ],
      'content',
      (content: WithUnifiedRowActions) => content.unifiedRowActions.length === 0,
    ),
  ];

  const headerShowActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.utils.separator('Show Actions', {
      tooltip:
        "'Bulk' and 'Other' actions share this setting. Collapsing one will collapse the other.",
    }),
    epc.fields.actionsDisplayModeField(
      'content.table.bulkActionsDisplayMode',
      '',
      { hideLabel: true, leftIndent: true },
      true,
    ),
  ];
  const hideHeaderShowActions = (
    content: WithUnifiedBulkActions & Partial<WithUnifiedOtherActions>,
  ): boolean => !content.unifiedBulkActions?.length && !content.unifiedOtherActions?.length;

  const bulkActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.fields.unifiedActionsField('content.unifiedBulkActions', 'bulk', {
      hasWorkflow: (action: PropertiesConfigInputContainerItemUnifiedBulkActionModel) =>
        action.type === 'export' || action.type === 'custom',
      workflowIdPath: (action: PropertiesConfigInputContainerItemUnifiedBulkActionModel) =>
        action.type === 'export' ? 'content.bulkExport.targetId' : undefined,
    }),
    ...epc.utils.conditionalFields(headerShowActionsGroup, 'content', hideHeaderShowActions),
  ];

  const otherActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.fields.unifiedActionsField('content.unifiedOtherActions', 'other', {
      hasWorkflow: () => true,
    }),
    ...epc.utils.conditionalFields(headerShowActionsGroup, 'content', hideHeaderShowActions),
  ];

  return epc.subsections.subsection(
    'content',
    'Actions',
    [
      ...commonActionsGroup,
      epc.fields.radiosField(
        'content.table.actionsView',
        '',
        [
          {
            value: 'row',
            label: 'Row',
            badge: (props: TablePropsModel) =>
              props.content.unifiedRowActions.length === 0
                ? undefined
                : props.content.unifiedRowActions.length.toString(),
          },
          {
            value: 'bulk',
            label: 'Bulk',
            badge: (props: TablePropsModel) =>
              props.content.unifiedBulkActions.length === 0
                ? undefined
                : props.content.unifiedBulkActions.length.toString(),
          },
          {
            value: 'other',
            label: 'Other',
            badge: (props: TablePropsModel) =>
              (props.content.unifiedOtherActions?.length ?? 0) === 0
                ? undefined
                : `${props.content.unifiedOtherActions?.length}`,
          },
        ],
        { hideLabel: true, leftIndent: true },
      ),
      ...epc.utils.disabledFields(
        [
          ...epc.utils.conditionalFields(
            rowActionsGroup,
            'content.table.actionsView',
            (actionsView) => actionsView !== 'row',
          ),
          ...epc.utils.conditionalFields(
            bulkActionsGroup,
            'content.table.actionsView',
            (actionsView) => actionsView !== 'bulk',
          ),
          ...epc.utils.conditionalFields(
            otherActionsGroup,
            'content.table.actionsView',
            (actionsView) => actionsView !== 'other',
          ),
        ],

        (model: TablePropsModel & CanvasElementPropertiesWithData) =>
          !getActiveBinding(model?.data, 'value')?.dataSource
            ? 'The Value of the element is not bound'
            : undefined,
      ),
    ],
    {
      hideWhen: (
        _elementId: CanvasElementId,
        _resolver: IPropEditorResolver,
        model: TablePropsModel,
      ) => model.content.table?.origin === 'wizard',
      lockWhen: (model: TablePropsModel) => {
        if (!model.data?.some((b) => isBindingActive(b))) {
          return 'Select a Data Source to enable Actions';
        }
        return undefined;
      },
    },
  );
}

function conditionalFormattingSubsection(): PropertiesConfigSubsection {
  return epc.subsections.subsection(
    'content.conditionalFormatting',
    'Conditional Formatting',
    [tableConditionalFormattingField('content.conditionalFormatting.formats')],
    {
      optional: true,
      tooltip:
        'Rules are evaluated from top to bottom for each individual cell, but only in the columns the rule is applied to. For each of those cells, only the first matching rule will be applied.',
      itemAdd: (model: PropsOf<'table'>): { path: string; value: unknown } | undefined => {
        if (!model.content.conditionalFormatting?.enabled) {
          return;
        }
        return {
          path: 'content.conditionalFormatting.formats',
          value: [...(model.content.conditionalFormatting?.formats ?? []), blankFormat()],
        };
      },
      lockWhen: (props: PropsOf<'table'>) =>
        getActiveBinding(props?.data, 'value') ? undefined : 'The Value of the element is bound',
    },
  );
}

function hasApiCallDataSource(resolver: IPropEditorResolver, props: TablePropsModel): boolean {
  return (
    props.data?.some(
      (b) =>
        isBindingActive(b) &&
        (b.dataSource?.sourceType === 'api-call' ||
          (b.dataSource?.sourceType === 'context' &&
            resolver.resolveValue('HAS_API_DATA_SOURCE', { workflowId: b.dataSource.id }))),
    ) ?? false
  );
}

export function blankFormat(): PropertiesConfigInputTableConditionalFormatModel {
  return {
    id: crypto.randomUUID(),
    label: { color1: '#373737', selectorType: 'solid' },
    fill: { color1: '#B9FBC0', selectorType: 'solid' },
    fontStyle: [],
    iconPosition: 'left',
    iconColor: { color1: '#373737', selectorType: 'solid' },
    columnSelection: TableConditionalFormatColumnSelection.selected,
    columns: [],
    conditions: { states: ['when'] },
  };
}

const tableConditionalFormattingField = (
  path: string,
): PropertiesConfigItemDefinition<'tableConditionalFormatting'> => ({
  path,
  label: '',
  inputType: 'tableConditionalFormatting',
});

const tableColumnsField = (
  path: string,
  dataSourcePath: string,
): PropertiesConfigItemDefinition<'tableColumns'> => ({
  path,
  inputType: 'tableColumns',
  label: '',
  inputOptions: {
    dataSourcePath,
    columnConfig: (index: number) => [
      epc.subsections.subsection(
        `${path}.${index}`,
        'Column Properties',
        getTableColumnPropertiesFields(path, index),
        { hideLabel: true, labelWidth: '40%' },
      ),
    ],
  },
});

function getTableColumnPropertiesFields(
  path: string,
  index: number,
): PropertiesConfigItemDefinition[] {
  return [
    // Title
    epc.fields.textInput(`${path}.${index}.label`, 'Title', {
      displayValue: epc.utils.elementMetaDisplayValue(`${path}.${index}.label`, 'title'),
    }),

    // Database field | when label diffs from column's name
    epc.utils.conditionalField(
      epc.fields.textInput(`${path}.${index}`, 'Database field', {
        readonly: true,
        displayValue: (elementId: CanvasElementId) => {
          const store = inject(Store);
          const schemaService = inject(SchemaService);
          return store.select(getElementById(elementId)).pipe(
            map((element) => {
              const column = getValueByPath<PropertiesConfigInputTableColumnModel>(
                (element as any)?.props,
                `${path}.${index}`,
              );
              if (!column) {
                return '';
              }
              // Display column resolved by ID if available
              return getColumnNameLatestOrUnavailable(column, schemaService);
            }),
            distinctUntilChanged(),
          );
        },
      }),
      `${path}.${index}`,
      (column) =>
        getValueByPath(column, 'label') === inject(SchemaService).getColumnNameLatest(column),
    ),

    // Type | non-'SecureText' format only
    epc.utils.conditionalField(
      epc.fields.selectFieldFromList(`${path}.${index}.format`, 'Type', TABLE_COLUMN_FORMATS, {
        showSelection: true,
        displayValue: epc.utils.elementMetaDisplayValue(`${path}.${index}.format`, 'format'),
      }),
      `${path}.${index}.format`,
      (_, model: PropsOf<'table'>) => {
        const schemaService = inject(SchemaService);
        const dataSource = getActiveBinding(model.data)?.dataSource;
        if (!dataSource) {
          return false;
        }
        let columns: DatasetColumn[] | undefined;
        schemaService
          .get(dataSource.id, dataSource.sourceType === 'context')
          .subscribe((schema) => {
            columns = schema?.columns;
          });

        const uiColumn = getValueByPath<PropertiesConfigInputTableColumnModel>(
          model,
          `${path}.${index}`,
        );
        const column = findColumn(columns, uiColumn ?? undefined);
        if (!column) {
          return false;
        }
        return isSecureTextColumn(column) ?? false;
      },
    ),

    // Precision | 'number', 'percentage', 'currency' formats only
    epc.utils.conditionalField(
      epc.fields.numberPrecisionField(`${path}.${index}.precision`, 'Precision', {
        defaultValue: null,
      }),
      `${path}.${index}.format`,
      (_, __, { fieldFormat: format }) =>
        !(format === 'number' || format === 'percentage' || format === 'currency'),
    ),

    // .currency | 'currency' format only
    epc.fields.currencyConditionalField(`${path}.${index}.currency`, `${path}.${index}.format`, {
      defaultValue: 'USD',
    }),

    // Duration format | 'duration' format only
    epc.utils.conditionalField(
      epc.fields.durationFormatField(`${path}.${index}.duration`, 'Duration format', {
        defaultValue: 'h:mm',
      }),
      `${path}.${index}.format`,
      (_, __, { fieldFormat: format }) => format !== 'duration',
    ),

    // Date format | 'date' format only
    epc.utils.conditionalField(
      epc.fields.dateFormatField(`${path}.${index}.dateFormat`, 'Date format'),
      `${path}.${index}.format`,
      (_, __, { fieldFormat: format }) => format !== 'date',
    ),

    // Date Time format | 'datetime' format only
    epc.utils.conditionalField(
      epc.fields.dateTimeFormatField(`${path}.${index}.dateTimeFormat`, 'Date Time format'),
      `${path}.${index}.format`,
      (_, __, { fieldFormat: format }) => format !== 'datetime',
    ),

    // .image | 'image' format only
    ...epc.utils.conditionalFields(
      epc.fields.imagePropertiesFields(
        `${path}.${index}.image`,
        {
          shape: 'auto',
          radius: '4px',
          outline: { color1: FALLBACK_THEME.value.outline, selectorType: 'solid' },
          outlineWidth: '0px',
        },
        undefined,
        undefined,
        true,
      ),
      `${path}.${index}.format`,
      (_, __, { fieldFormat: format }) => format !== 'image',
    ),

    // .initials | 'initials' format only (either as column type or fallback)
    ...epc.utils.conditionalFields(
      [
        epc.utils.conditionalField(
          epc.fields.selectFieldFromDataBinding(
            `${path}.${index}.image.initialsColumn`,
            '',
            'value',
            {
              dataBindingsMode: 'first-dataset',
              valueField: COLUMN_ID_OR_NAME,
              allowNone: false,
              optionsFilter: epc.utils.columnDataTypeFilter('String'),
            },
          ),

          `${path}.${index}.format`,
          (format) => format !== 'initials',
        ),
        epc.utils.separatedField(
          epc.fields.colorField(`${path}.${index}.initials.background`, 'Background', {
            allowGradient: false,
            allowNone: false,
            defaultValue: (model: TablePropsModel) =>
              model.style?.table?.lines ?? { color1: '$t.fillInternal', selectorType: 'solid' },
          }),
        ),
        epc.fields.colorField(`${path}.${index}.initials.textColor`, 'Initials', {
          allowGradient: false,
          allowNone: false,
          defaultValue: (model: TablePropsModel) =>
            model.style?.table?.text ?? { color1: '$t.value', selectorType: 'solid' },
        }),

        epc.fields.selectFieldFromFormFonts(`${path}.${index}.initials.font.fontFamily`, 'Font', {
          defaultValue: (model: TablePropsModel) => model.style?.fontFamily ?? '$t.fontFamily',
        }),
        epc.fields.selectFieldFromFontWeights(
          `${path}.${index}.initials.font.fontWeight`,
          'Weight',
          `${path}.${index}.initials.font.fontFamily`,
        ),

        epc.fields.sliderField(`${path}.${index}.initials.font.fontSize`, 'Size', {
          units: [{ min: 8, max: 72, unit: 'px' }],
          defaultValue: (model: TablePropsModel) => model.style?.fontSize ?? '$t.fontSize',
        }),
      ],
      `${path}.${index}`,
      (model, _, { fieldFormat: format }) => {
        const showInitialsStyles =
          (format === 'image' && model?.image?.emptyThumbnail === 'initials') ||
          format === 'initials';
        return !showInitialsStyles;
      },
    ),

    // .align | non-'progressBar' formats only
    epc.utils.conditionalField(
      epc.fields.horizontalAlignField(`${path}.${index}.align`),
      `${path}.${index}.format`,
      (_, __, { fieldFormat: format }) => format === 'progressBar',
    ),

    // progressBar config | 'progressBar' format only
    ...epc.utils.conditionalFields(
      progressBarConfigFields(`${path}.${index}.progressBar`),
      `${path}.${index}.format`,
      (_, __, { fieldFormat: format }) => format !== 'progressBar',
    ),

    // Show column
    epc.utils.separatedField(
      epc.fields.toggleField(`${path}.${index}.visible`, 'Show column', { renderType: 'check' }),
    ),

    // Enable In-line Editing
    epc.utils.conditionalField(
      epc.fields.toggleField(`${path}.${index}.editable`, 'Enable In-line Editing', {
        renderType: 'check',
      }),
      `${path}.${index}`,
      (target, model, _syncModel) => {
        const isTableEditable = !!getValueByPath(model, 'content.table.editable');
        if (!isTableEditable) {
          return true;
        }
        const activeBinding = getActiveBinding(model.data);
        const activeBindingColumns = activeBinding?.dataSource?.columns;

        const runtimeService = inject(RuntimeService);

        const editableColumnsKeys = runtimeService.getEditableColumns(model.data);

        const column = findColumn(activeBindingColumns, target);
        if (isColumnEditable(column, editableColumnsKeys)) {
          return false;
        }
        return true;
      },
    ),

    // Open page on click | 'tag' format on 'link' column only
    epc.utils.conditionalField(
      epc.fields.toggleField(`${path}.${index}.onLinkClick.enabled`, 'Open page on click', {
        renderType: 'check',
      }),
      `${path}.${index}`,
      (target, model, _syncModel) => {
        const activeBinding = getActiveBinding(model.data);
        const activeBindingColumns = activeBinding?.dataSource?.columns;
        const column = findColumn(activeBindingColumns, target);
        return target?.format !== 'tag' || column?.columnType !== 'Link';
      },
    ),

    // Select page | 'tag' format on 'link' column only
    epc.utils.conditionalField(
      epc.fields.selectFieldFromFormPages(`${path}.${index}.onLinkClick.page`, '', {
        placeholder: 'Select page',
        emptyMessage: 'No applicable pages created',
        allowNone: false,
        hideLabel: true,
        layoutMode: 'narrow-left-margin-check',
        optionsFilter: (model, options) => {
          const activeBinding = getActiveBinding(model.data);
          const activeBindingColumns = activeBinding?.dataSource?.columns;

          const target = getValueByPath(model, `${path}.${index}`) as ColumnQueryLike;
          const column = findColumn(activeBindingColumns, target);
          const relatedTableId = column?.link?.tableId;

          return options.filter(
            (opt: any) =>
              ('mainDatasetId' in opt && relatedTableId?.includes(opt.mainDatasetId)) || false,
          );
        },
      }),
      `${path}.${index}`,
      (target, model, _syncModel) => {
        const activeBinding = getActiveBinding(model.data);
        const activeBindingColumns = activeBinding?.dataSource?.columns;
        const column = findColumn(activeBindingColumns, target);

        return (
          target?.format !== 'tag' || column?.columnType !== 'Link' || !target?.onLinkClick?.enabled
        );
      },
    ),

    // Palette | 'tag' format only
    epc.utils.conditionalField(
      epc.fields.tagsColorPaletteField(`${path}.${index}.tagsPalette`, 'Palette', {
        dataSourceKey: `${path}.${index}`,
        hideLabel: false,
        defaultValue: { ...DEFAULT_COLUMN_TAGS_PALETTE },
      }),
      `${path}.${index}.format`,
      (_, __, { fieldFormat: format }) => format !== 'tag',
    ),
  ];
}

function progressBarConfigFields(path: string): PropertiesConfigItemDefinition[] {
  return [
    epc.fields.textInput(`${path}.min`, 'Min value', { inputType: 'number', placeholder: 'Auto' }),
    epc.fields.textInput(`${path}.max`, 'Max value', { inputType: 'number', placeholder: 'Auto' }),
    epc.fields.radiosField(
      `${path}.extras`,
      'Show value',
      [
        { label: '%', value: 'percent' },
        { label: 'Value', value: 'value' },
        { label: 'None', value: 'none' },
      ],
      { defaultValue: 'percent' },
    ),
    ...epc.utils.conditionalFields(
      [
        epc.fields.selectFieldFromList(`${path}.format`, 'Format', NUMBER_FIELD_FORMATS_OPTIONS, {
          showSelection: true,
          allowNone: false,
          defaultValue: 'number',
        }),
        epc.fields.numberPrecisionField(`${path}.precision`, 'Precision', { defaultValue: 1 }),
        epc.fields.currencyConditionalField(`${path}.currency`, `${path}.format`, {
          allowNone: false,
          showSelection: true,
          defaultValue: 'USD',
        }),
      ],
      `${path}.extras`,
      (extras) => extras !== 'value',
    ),
    ...epc.utils.conditionalFields(
      [
        epc.fields.horizontalAlignField(`${path}.align`, {
          defaultValue: 'left',
          icons: ['icon_aligns_to_left_16', 'icon_aligns_to_right_16', 'icon_aligns_to_center_16'],
        }),
        epc.fields.radiosField(
          `${path}.valueColor`,
          'Value color',
          [
            { label: 'Default', value: 'default' },
            { label: 'Alert', value: 'alert' },
          ],
          { defaultValue: 'default' },
        ),
      ],
      `${path}.extras`,
      (extras) => extras === 'none',
    ),
    epc.utils.separatedField(colorScaleField(path), {
      hideSeparatorWhenHidden: true,
    }),
  ];
}

const colorScaleField = (basePath: string): PropertiesConfigItemDefinition<'colorScale'> => ({
  path: `${basePath}.colorScale`,
  inputType: 'colorScale',
  label: 'Color Scale',
  inputOptions: {
    minValuePath: `${basePath}.min`,
    maxValuePath: `${basePath}.max`,
    unitPath: `${basePath}.colorScaleUnit`,
    units: [
      { min: 0, max: 100, unit: '%', step: 1 },
      { min: 0, max: Number.MAX_SAFE_INTEGER, unit: 'num', step: 10 },
    ],
    defaultValue: [
      { color: { color1: PROGRESS_BAR_THRESHOLDS_COLORS[0] }, value: 25 },
      { color: { color1: PROGRESS_BAR_THRESHOLDS_COLORS[1] }, value: 50 },
      { color: { color1: PROGRESS_BAR_THRESHOLDS_COLORS[2] }, value: 75 },
    ],
  },
});
